/**
 * **주문 고객 관리의 쿠폰 발송 — 고를 수 있는 할인과 안내 한 곳** — 의존성 없는 순수 모듈이다(단위 테스트가 node 에서 그대로
 * 읽는다).
 *
 * 발송 API(`POST /telInfo/sendCoupon`)의 2xx 는 쿠폰을 만들고 알림톡 전송을 **맡겼다**는 뜻이다(응답 `sendStatus: PENDING`).
 * 알림톡은 appserver 가 뒤에서 비동기로 보내고 실패할 수 있다 — 그때 쿠폰의 `send_status` 가 나중에 FAILED 가 된다(R-L V1-8
 * compose 실측: 요청 뒤 0.1초에 FAILED). 그래서 화면은 「발송되었습니다」가 아니라 **「발송을 요청했습니다」**라고 말한다.
 * 발송 결과(`send_status`)를 보여 주는 어드민 화면은 아직 없다 — 생기면 여기 안내를 붙인다(새 폴링은 두지 않는다).
 */
import { isCouponOnlyDiscount } from './discountOffer.js'

/** 알림톡이 뒤에서 나간다는 한 줄 — 단건·일괄이 같은 말을 한다 */
const DELIVERY_NOTE = '알림톡 전송은 잠시 뒤 처리됩니다.'

/**
 * 할인 고르기 칸 아래 한 줄 — 단건·일괄이 같은 말을 한다. 쿠폰은 **쿠폰 전용 할인으로만** 낸다(KT R-T 결정 ①, 2026-10-12):
 * 상시할인으로 쿠폰을 내면 그 할인이 주문에 자동으로 한 번, 쿠폰으로 한 번 더 깎인다. 종전에는 쿠폰 사용 가능 할인을 모두
 * 골랐으므로 바뀐 사실을 이 창에서만 말한다.
 *
 * 고를 할인이 없을 때의 할 일은 쿠폰 전용 할인을 **따로 만드는 것**이다 — 있는 상시할인을 쿠폰 전용으로 바꾸라고 말하지
 * 않는다(바꾸면 그 할인이 주문에 자동으로 걸리지 않게 된다). 우리동네TV 패널의 같은 사유 문장(`tvCoupon.js`)과 같은 말이다.
 */
export const COUPON_SEND_TEXT = Object.freeze({
  couponOnlyRule: '쿠폰은 쿠폰 전용 할인으로만 보낼 수 있습니다.',
  noCouponOnlyDiscount: "보낼 수 있는 쿠폰 전용 할인이 없습니다. 할인 관리에서 '쿠폰 전용' 할인을 먼저 만드세요.",
})

/**
 * 쿠폰으로 보낼 수 있는 할인 — 사용 중인 **쿠폰 전용** 할인. 판정은 [isCouponOnlyDiscount] 한 곳(서버 `DiscountCouponOnly.of`
 * 와 같은 식 — 목록 행의 `couponApplicableYn`·`couponOnlyYn` 두 칸)이다. 서버 발급 관문도 같은 술어로 거절하고, 그 문장은
 * 인터셉터가 서버 문장 그대로 띄운다 — 화면은 고를 수 없는 할인을 내놓지 않을 뿐이다.
 *
 * @param {Array<object>} discounts `GET /discount/store` 응답 행
 * @returns {Array<object>} 새 배열(원본은 그대로)
 */
export function couponSendableDiscounts(discounts) {
  return discounts.filter((discount) => discount.useYn === 'Y' && isCouponOnlyDiscount(discount))
}

/**
 * 할인 고르기 칸 아래 한 줄 — 목록을 아직 못 읽었으면(`null`) 말하지 않는다(모르는 것을 「없다」고 하지 않는다).
 *
 * @param {?Array<object>} sendable [couponSendableDiscounts] 결과(읽기 전·읽는 중·읽기 실패는 `null`)
 * @returns {?string}
 */
export function couponSendDiscountNote(sendable) {
  if (!sendable) return null

  return sendable.length > 0 ? COUPON_SEND_TEXT.couponOnlyRule : COUPON_SEND_TEXT.noCouponOnlyDiscount
}

/**
 * 단건 발송 요청 안내.
 *
 * @param {string} customerName 고객 이름(없으면 부르는 쪽이 「고객」을 넘긴다)
 */
export function couponSendRequestedMessage(customerName) {
  return `${customerName}님에게 쿠폰 발송을 요청했습니다. ${DELIVERY_NOTE}`
}

/**
 * 일괄 발송 요청 안내.
 *
 * @param {number} count 마케팅 동의 고객 수
 */
export function bulkCouponSendRequestedMessage(count) {
  return `${count}명의 고객에게 쿠폰 일괄 발송을 요청했습니다. ${DELIVERY_NOTE}`
}
